import { safeSanitize } from '../../utils/safe_sanitize';

angular.module("LeasePilot").controller("AuditTrailController", [
  "$scope",
  "$http",
  "$filter",
  "$sce",
  "$document",
  function($scope, $http, $filter, $sce, $document) {
    $scope.rawEvents = []; // Store original raw events
    $scope.events = [];
    $scope.filteredEvents = [];
    $scope.loading = false;
    $scope.error = null;

    // Filter panel state
    $scope.activeFilter = null;
    $scope.filterPosition = {};

    // Timezone support
    $scope.timezones = [
      { id: "UTC", name: "UTC" },
      { id: "America/New_York", name: "Eastern Time" },
      { id: "America/Chicago", name: "Central Time" },
      { id: "America/Denver", name: "Mountain Time" },
      { id: "America/Los_Angeles", name: "Pacific Time" },
      { id: "America/Anchorage", name: "Alaska Time" },
      { id: "Pacific/Honolulu", name: "Hawaii Time" }
    ];

    // Default to browser's timezone
    $scope.filters = {
      startDate: null,
      endDate: null,
      selectedUsers: [],
      selectedEvents: [],
      selectedTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    if (
      !$scope.timezones.find(tz => tz.id === $scope.filters.selectedTimezone)
    ) {
      $scope.filters.selectedTimezone = "UTC";
    }

    // Search text for autocomplete
    $scope.userSearchText = "";
    $scope.eventSearchText = "";

    // Autocomplete data
    $scope.users = [];
    $scope.eventTypes = [];

    function convertToTimezone(date, timezone) {
      if (!date) return null;

      // Get the UTC timestamp
      const utcDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );

      // Convert to target timezone
      const targetDate = new Date(
        utcDate.toLocaleString("en-US", { timeZone: timezone })
      );
      const offset = targetDate.getTime() - utcDate.getTime();

      return new Date(date.getTime() + offset);
    }

    function sanitizeText(text) {
      if (!text || typeof text !== 'string') return text;
      // Remove Angular interpolation markers first
      let cleanText = text.replace(/{{.*?}}/g, '');
      // Sanitize HTML tags and attributes - using sanitizeHtml which is globally available
      cleanText = safeSanitize(cleanText, {
        allowedTags: [],
        allowedAttributes: [],
      });
      return cleanText;
    }

    function getUserDisplayName(userEmail) {
      if (!userEmail || !window.users) return userEmail;
      const user = window.users.find(u => u.email === userEmail);
      if (user && user.firstName && user.lastName) {
        return sanitizeText(`${user.firstName} ${user.lastName}`);
      }
      return sanitizeText(userEmail);
    }

    function getEventDescription(event, metadata) {
      try {
        const EventClass = window.Events[event];
        if (EventClass) {
          const description = new EventClass(metadata).description;
          return sanitizeText(description);
        }
        return sanitizeText(event);
      } catch (e) {
        console.error("Error getting event description:", e);
        return sanitizeText(event);
      }
    }

    function processEvent(event) {
      const metadata = (() => {
        try {
          if (typeof event.metadata === "object") return event.metadata;
          if (typeof event.metadata === "string") {
            const parsed = JSON.parse(event.metadata);
            return parsed;
          }
          return event.metadata;
        } catch (e) {
          console.error("Error parsing metadata:", e);
          return event.metadata;
        }
      })();

      const userDisplayName = getUserDisplayName(event.user_email);
      const eventDescription = getEventDescription(event.event, metadata);

      const eventMetadata = (() => {
        try {
          const EventClass = window.Events[event.event];
          if (EventClass) {
              const formatted = new EventClass(metadata).format();
              if (formatted) {
                // Sanitize the HTML content while preserving allowed HTML tags for formatting
                const sanitized = safeSanitize(formatted, {
                  allowedTags: ['em', 'strong', 'i', 'b', 'u', 'p', 'br', 'span', 'ins', 'del', 'a'],
                  allowedAttributes: []
                });
                return $sce.trustAsHtml(sanitized);
              }
            return null;
          }
          return $sce.trustAsHtml(sanitizeText(event.event));
        } catch (e) {
          console.error("Error getting event description:", e);
          return $sce.trustAsHtml(sanitizeText(event.event));
        }
      })();

      const timestamp = new Date(event.timestamp);
      const localTimestamp = convertToTimezone(
        timestamp,
        $scope.filters.selectedTimezone
      );

      return {
        ...event,
        processedMetadata: metadata,
        userDisplayName,
        eventDescription,
        eventMetadata,
        timestamp: localTimestamp,
        originalTimestamp: timestamp
      };
    }

    // Helper function to consolidate events
    function consolidateEvents(events) {
      if (!events || events.length === 0) return [];

      const consolidated = [];

      let currentEvent = events[0];
      // Parse metadata once at the start
      let currentMetadata = typeof currentEvent.metadata === "string" 
        ? JSON.parse(currentEvent.metadata) 
        : currentEvent.metadata;

      for (let i = 1; i < events.length; i++) {
        if (currentEvent.event === events[i].event) {
          const currentEventInstance = (() => {
            const EventClass = window.Events[currentEvent.event];
            return EventClass
              ? new EventClass(currentMetadata)
              : currentEvent;
          })();

          // Parse next event's metadata once
          const nextMetadata = typeof events[i].metadata === "string"
            ? JSON.parse(events[i].metadata)
            : events[i].metadata;

          const nextEventInstance = (() => {
            const EventClass = window.Events[events[i].event];
            return EventClass
              ? new EventClass(nextMetadata)
              : events[i];
          })();
          
          if (currentEventInstance.compare(nextEventInstance)) {
            currentEvent.metadata = currentEventInstance.consolidate(nextEventInstance).data;
          } else {
            consolidated.push(currentEvent);
            currentEvent = events[i];
            // Update current metadata for next iteration
            Object.assign(currentMetadata, nextMetadata);
          }
        } else {
          consolidated.push(currentEvent);
          currentEvent = events[i];
          // Update current metadata for next iteration
          currentMetadata = typeof currentEvent.metadata === "string"
            ? JSON.parse(currentEvent.metadata)
            : currentEvent.metadata;
        }
      }
      consolidated.push(currentEvent);

      return consolidated.map(processEvent);
    }

    function processAndFilterEvents() {
      // Create a new array for filtered events
      let newFilteredEvents = [];

      // Process events in a temporary array first
      const processedEvents = $scope.rawEvents.map(event => ({
        ...event,
        timestamp: new Date(event.timestamp)
      }));

      // Filter events
      const filtered = processedEvents.filter(function(event) {
        // Date range filter
        if (
          $scope.filters.startDate &&
          event.timestamp < $scope.filters.startDate
        )
          return false;
        if (
          $scope.filters.endDate &&
          event.timestamp > $scope.filters.endDate
        )
          return false;

        // User filter
        if (
          $scope.filters.selectedUsers.length > 0 &&
          !$scope.filters.selectedUsers.some(
            user => user.value === event.user_email
          )
        ) {
          return false;
        }

        // Event type filter
        if (
          $scope.filters.selectedEvents.length > 0 &&
          !$scope.filters.selectedEvents.some(
            eventType => eventType.value === event.event
          )
        ) {
          return false;
        }

        return true;
      });

      // Use $scope.$apply to ensure we update the view in a single digest cycle
      $scope.$evalAsync(() => {
        // Clear the current array
        $scope.filteredEvents = [];

        // Process and consolidate events
        $scope.filteredEvents = consolidateEvents(filtered);
      });
    }

    $scope.onTimezoneChange = function() {
      $scope.$evalAsync(() => {
        processAndFilterEvents();
      });
    };

    // Add document click handler
    function handleDocumentClick(event) {
      // If we're not clicking on a filter button or filter panel
      if (
        !event.target.closest(".filter-button") &&
        !event.target.closest(".filter-panel")
      ) {
        $scope.$apply(() => {
          $scope.activeFilter = null;
        });
      }
    }

    // Register and clean up document click handler
    $document.on("click", handleDocumentClick);
    $scope.$on("$destroy", function() {
      $document.off("click", handleDocumentClick);
    });

    $scope.toggleFilter = function(filterType, event) {
      if (event) {
        event.stopPropagation();

        // Get the button's position
        const button = event.currentTarget;
        const buttonRect = button.getBoundingClientRect();

        // Calculate position for the filter panel
        const position = {
          top: buttonRect.bottom + 4 + "px",
          left: buttonRect.left + "px"
        };

        // Check if the panel would go off-screen to the right
        const panelWidth = 280; // min-width of the panel
        if (buttonRect.left + panelWidth > window.innerWidth) {
          position.left = window.innerWidth - panelWidth - 16 + "px"; // 16px padding from the right
        }

        $scope.filterPosition = position;
      }

      // Close if clicking the same filter, otherwise switch to the new filter
      $scope.activeFilter =
        $scope.activeFilter === filterType ? null : filterType;
    };

    $scope.toggleUserFilter = function(user) {
      // Update the selected users in filters
      if (user.selected) {
        $scope.filters.selectedUsers.push(user);
      } else {
        $scope.filters.selectedUsers = $scope.filters.selectedUsers.filter(
          u => u.value !== user.value
        );
      }
    };

    $scope.toggleEventFilter = function(event) {
      // Update the selected events in filters
      if (event.selected) {
        $scope.filters.selectedEvents.push(event);
      } else {
        $scope.filters.selectedEvents = $scope.filters.selectedEvents.filter(
          e => e.value !== event.value
        );
      }
    };

    $scope.clearUserFilter = function() {
      $scope.filters.selectedUsers = [];
      $scope.users.forEach(user => {
        user.selected = false;
      });
    };

    $scope.clearEventFilter = function() {
      $scope.filters.selectedEvents = [];
      $scope.eventTypes.forEach(event => {
        event.selected = false;
      });
    };

    $scope.clearTimeFilter = function() {
      $scope.filters.startDate = null;
      $scope.filters.endDate = null;
    };

    $scope.hasTimeFilter = function() {
      return $scope.filters.startDate || $scope.filters.endDate;
    };

    $scope.getTimeFilterDescription = function() {
      if ($scope.filters.startDate && $scope.filters.endDate) {
        return "Range";
      } else if ($scope.filters.startDate) {
        return "After";
      } else if ($scope.filters.endDate) {
        return "Before";
      }
      return "";
    };

    $scope.hasActiveFilters = function() {
      return (
        $scope.hasTimeFilter() ||
        $scope.filters.selectedUsers.length > 0 ||
        $scope.filters.selectedEvents.length > 0
      );
    };

    $scope.clearAllFilters = function() {
      $scope.filters.startDate = null;
      $scope.filters.endDate = null;
      $scope.clearUserFilter();
      $scope.clearEventFilter();
    };

    $scope.loadEvents = function() {
      $scope.loading = true;
      $scope.error = null;

      $http
        .get("/api/audit_trail")
        .then(function(response) {
          // Store raw events and sort by timestamp
          $scope.rawEvents = response.data.data;

          // Initialize autocomplete data
          // Create unique users with full data
          const uniqueUsers = new Map();
          for (let i = 0; i < $scope.rawEvents.length; i++) {
            const event = $scope.rawEvents[i];
            event.id = Math.random()
              .toString(36)
              .substring(2);

            if (!uniqueUsers.has(event.user_email)) {
              uniqueUsers.set(event.user_email, {
                value: sanitizeText(event.user_email),
                display: getUserDisplayName(event.user_email),
                selected: false
              });
            }
          };
          $scope.users = Array.from(uniqueUsers.values());

          // Create unique event types with full data
          const uniqueEvents = new Map();
          for (let i = 0; i < $scope.rawEvents.length; i++) {
            const event = $scope.rawEvents[i];
            if (!uniqueEvents.has(event.event)) {
              const metadata = (() => {
                try {
                  if (typeof event.metadata === "object")
                    return event.metadata;
                  if (typeof event.metadata === "string") {
                    const parsed = JSON.parse(event.metadata);
                    return parsed;
                  }
                  return event.metadata;
                } catch (e) {
                  console.error("Error parsing metadata:", e);
                  return event.metadata;
                }
              })();
              uniqueEvents.set(event.event, {
                value: sanitizeText(event.event),
                display: getEventDescription(event.event, metadata),
                selected: false
              });
            }
          };
          $scope.eventTypes = Array.from(uniqueEvents.values());

          // Process and apply initial filters
          processAndFilterEvents();
          $scope.loading = false;
        })
        .catch(function(error) {
          $scope.error = "Failed to load audit trail events";
          console.error(error);
          $scope.loading = false;
        });
    };

    // Watch for filter changes
    $scope.$watch(
      "filters",
      function() {
        processAndFilterEvents();
      },
      true
    );

    // Initialize
    $scope.loadEvents();
  }
]);
